import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import DailyIframe from '@daily-co/daily-js';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import { useAuth } from '../contexts/AuthContext';
import { Video, MessageCircle, Send, ArrowLeft, Users, ExternalLink, Monitor, Mic, MicOff, VideoIcon, VideoOff, PhoneOff, Calendar, Clock, PanelRightClose, PanelRightOpen, Maximize2, Minimize2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LiveClass() {
  const { classId } = useParams();
  const navigate = useNavigate();
  const { user, getAuthHeader } = useAuth();
  const [liveClass, setLiveClass] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  
  // Daily.co states for embedded streaming
  const [callObject, setCallObject] = useState(null);
  const [callState, setCallState] = useState('idle'); // idle, joining, joined, left, error
  const [isMicOn, setIsMicOn] = useState(true);
  const [isCamOn, setIsCamOn] = useState(true);
  const [participants, setParticipants] = useState([]);
  const videoContainerRef = useRef(null);
  
  // Chat minimize and orientation states
  const [isChatMinimized, setIsChatMinimized] = useState(false);
  const [isLandscape, setIsLandscape] = useState(window.innerWidth > window.innerHeight);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);

  useEffect(() => {
    fetchClassData();
    
    // Handle orientation changes
    const handleOrientationChange = () => {
      const landscape = window.innerWidth > window.innerHeight;
      const mobile = window.innerWidth < 1024;
      setIsLandscape(landscape);
      setIsMobile(mobile);
      // Auto-minimize chat and go fullscreen in landscape on mobile for max video space
      if (landscape && mobile) {
        setIsChatMinimized(true);
        setIsFullscreen(true);
      } else if (!landscape && mobile) {
        // Exit fullscreen when returning to portrait on mobile
        setIsFullscreen(false);
      }
    };
    
    // Handle resize events
    window.addEventListener('resize', handleOrientationChange);
    window.addEventListener('orientationchange', handleOrientationChange);
    
    // Initial check
    handleOrientationChange();
    
    return () => {
      // Cleanup Daily call on unmount
      if (callObject) {
        callObject.destroy();
      }
      window.removeEventListener('resize', handleOrientationChange);
      window.removeEventListener('orientationchange', handleOrientationChange);
    };
  }, [classId]);

  const fetchClassData = async () => {
    try {
      const response = await axios.get(`${API}/live-classes/${classId}`, getAuthHeader());
      setLiveClass(response.data);
    } catch (error) {
      toast.error('Failed to load class');
    } finally {
      setLoading(false);
    }
  };

  // Initialize Daily.co call for embedded streaming
  const initializeEmbeddedCall = useCallback(async () => {
    if (!liveClass || liveClass.stream_type !== 'embedded') return;
    
    try {
      setCallState('joining');
      
      // Create Daily call frame (prebuilt UI)
      const call = DailyIframe.createFrame(videoContainerRef.current, {
        iframeStyle: {
          width: '100%',
          height: '100%',
          border: '0',
          borderRadius: '12px',
        },
        showLeaveButton: true,
        showFullscreenButton: true,
      });

      // Set up event listeners
      call.on('joined-meeting', () => {
        setCallState('joined');
        toast.success('Joined the live class!');
      });

      call.on('left-meeting', () => {
        setCallState('left');
        toast.info('You left the class');
      });

      call.on('error', (e) => {
        console.error('Daily error:', e);
        setCallState('error');
        toast.error('Failed to connect to the class');
      });

      call.on('participant-joined', (event) => {
        setParticipants(prev => [...prev, event.participant]);
      });

      call.on('participant-left', (event) => {
        setParticipants(prev => prev.filter(p => p.session_id !== event.participant.session_id));
      });

      setCallObject(call);

      // Join the room using the room_url from the backend
      const roomUrl = liveClass.room_url.startsWith('http') 
        ? liveClass.room_url 
        : `https://yourdomain.daily.co${liveClass.room_url}`;
      
      await call.join({ 
        url: roomUrl,
        userName: user?.name || 'Student'
      });

    } catch (error) {
      console.error('Error initializing Daily call:', error);
      setCallState('error');
      toast.error('Failed to initialize video call');
    }
  }, [liveClass, user]);

  const toggleMic = () => {
    if (callObject) {
      callObject.setLocalAudio(!isMicOn);
      setIsMicOn(!isMicOn);
    }
  };

  const toggleCam = () => {
    if (callObject) {
      callObject.setLocalVideo(!isCamOn);
      setIsCamOn(!isCamOn);
    }
  };

  const leaveCall = async () => {
    if (callObject) {
      await callObject.leave();
      callObject.destroy();
      setCallObject(null);
      setCallState('left');
    }
  };

  const sendMessage = () => {
    if (newMessage.trim()) {
      setMessages([
        ...messages,
        {
          id: Date.now(),
          user: user?.name || 'Anonymous',
          message: newMessage,
          timestamp: new Date().toISOString()
        }
      ]);
      setNewMessage('');
    }
  };

  const formatScheduledDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const formatScheduledTime = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!liveClass) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <p className="text-slate-600 mb-4">Class not found</p>
          <Button onClick={() => navigate(-1)}>Go Back</Button>
        </div>
      </div>
    );
  }

  // Render External Link Class Type
  if (liveClass.stream_type === 'external') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-indigo-50">
        {/* Header */}
        <header className="bg-white border-b border-slate-200 shadow-sm">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center h-16">
              <Button
                data-testid="back-to-dashboard-btn"
                variant="ghost"
                size="sm"
                onClick={() => navigate(-1)}
                className="text-slate-600 hover:text-slate-900"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Card className="bg-white rounded-2xl border border-slate-100 shadow-lg overflow-hidden">
            {/* Class Header Banner */}
            <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-8 py-10 text-white">
              <div className="flex items-center space-x-2 mb-4">
                <ExternalLink className="w-5 h-5" />
                <span className="text-indigo-200 text-sm font-medium">External Meeting</span>
              </div>
              <h1 className="text-3xl font-bold mb-2">{liveClass.title}</h1>
              <p className="text-indigo-100 text-lg">{liveClass.description}</p>
              {liveClass.teacher_name && (
                <p className="text-indigo-200 mt-2">Instructor: {liveClass.teacher_name}</p>
              )}
            </div>

            {/* Meeting Details */}
            <div className="p-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div className="flex items-center space-x-4 p-4 bg-slate-50 rounded-xl">
                  <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center">
                    <Calendar className="w-6 h-6 text-indigo-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Scheduled Date</p>
                    <p className="text-lg font-semibold text-slate-900">
                      {formatScheduledDate(liveClass.scheduled_at)}
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-4 p-4 bg-slate-50 rounded-xl">
                  <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                    <Clock className="w-6 h-6 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Time & Duration</p>
                    <p className="text-lg font-semibold text-slate-900">
                      {formatScheduledTime(liveClass.scheduled_at)} ({liveClass.duration_minutes} min)
                    </p>
                  </div>
                </div>
              </div>

              {/* Meeting Link Section */}
              <div className="bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-100 rounded-xl p-6 mb-6">
                <h3 className="text-lg font-bold text-slate-900 mb-2 flex items-center">
                  <Video className="w-5 h-5 mr-2 text-indigo-600" />
                  Join Live Class
                </h3>
                <p className="text-slate-600 text-sm mb-4">
                  Click the button below to join the live class session.
                </p>
                <a
                  href={liveClass.external_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="join-external-meeting-btn"
                  className="inline-flex items-center justify-center px-8 py-4 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold rounded-xl shadow-lg shadow-indigo-500/30 hover:shadow-indigo-500/50 hover:-translate-y-0.5 transition-all duration-200"
                >
                  <ExternalLink className="w-5 h-5 mr-2" />
                  Join Meeting Now
                </a>
              </div>

              {/* Instructions */}
              <div className="text-sm text-slate-500">
                <p className="mb-2">Instructions:</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>Click "Join Meeting Now" to open the meeting in a new tab</li>
                  <li>Make sure your camera and microphone are enabled</li>
                  <li>Join a few minutes early to test your connection</li>
                </ul>
              </div>
            </div>
          </Card>
        </main>
      </div>
    );
  }

  // Render Embedded Class Type (Daily.co)
  return (
    <div className={`h-screen bg-slate-900 flex flex-col ${isFullscreen ? 'fixed inset-0 z-50' : ''}`}>
      {/* Header - Hidden in fullscreen */}
      {!isFullscreen && (
        <header className="bg-slate-800 border-b border-slate-700 flex-shrink-0">
          <div className="px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-14 sm:h-16">
              <div className="flex items-center space-x-2 sm:space-x-4">
                <Button
                  data-testid="back-to-dashboard-btn"
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate(-1)}
                  className="text-slate-300 hover:text-white p-2"
                >
                  <ArrowLeft className="w-4 h-4" />
                  <span className="hidden sm:inline ml-2">Back</span>
                </Button>
                <div className="truncate">
                  <h1 className="text-sm sm:text-lg font-bold text-white truncate">{liveClass.title}</h1>
                  <p className="text-xs text-slate-400 hidden sm:block">Teacher: {liveClass.teacher_name}</p>
                </div>
              </div>
              <div className="flex items-center space-x-2 sm:space-x-4">
                <div className="flex items-center space-x-1 sm:space-x-2 text-slate-300">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                  <span className="text-xs sm:text-sm">{callState === 'joined' ? 'Live' : '...'}</span>
                </div>
                <div className="flex items-center space-x-1 sm:space-x-2 text-slate-300">
                  <Users className="w-4 h-4" />
                  <span className="text-xs sm:text-sm">{participants.length + 1}</span>
                </div>
              </div>
            </div>
          </div>
        </header>
      )}

      {/* Main Content - Responsive Layout */}
      <div className={`flex-1 flex ${isLandscape && !isChatMinimized ? 'flex-row' : 'flex-col'} gap-2 sm:gap-4 p-2 sm:p-4 overflow-hidden`}>
        {/* Video Area - Expands when chat is minimized */}
        <div className={`bg-black rounded-xl overflow-hidden relative transition-all duration-300 ${
          isChatMinimized 
            ? 'flex-1' 
            : isLandscape 
              ? 'flex-1 lg:flex-[3]' 
              : 'flex-1 min-h-[50vh] sm:min-h-[60vh]'
        }`}>
          {callState === 'idle' && (
            <div className="absolute inset-0 flex flex-col items-center justify-center p-4">
              <div className="text-center">
                <div className="w-16 h-16 sm:w-20 sm:h-20 bg-indigo-600/20 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6">
                  <Monitor className="w-8 h-8 sm:w-10 sm:h-10 text-indigo-400" />
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-white mb-2">Ready to Join?</h3>
                <p className="text-slate-400 mb-4 sm:mb-6 max-w-md text-sm sm:text-base">
                  Click the button below to join the live video class.
                </p>
                <Button
                  onClick={initializeEmbeddedCall}
                  data-testid="join-embedded-class-btn"
                  className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 sm:px-8 py-2 sm:py-3 rounded-xl text-base sm:text-lg font-semibold shadow-lg shadow-indigo-500/30"
                >
                  <Video className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                  Join Live Class
                </Button>
              </div>
            </div>
          )}

          {callState === 'joining' && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center">
                <div className="animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-b-2 border-indigo-500 mx-auto mb-4"></div>
                <p className="text-slate-400 text-sm sm:text-base">Connecting to class...</p>
              </div>
            </div>
          )}

          {callState === 'error' && (
            <div className="absolute inset-0 flex flex-col items-center justify-center p-4">
              <div className="text-center">
                <div className="w-14 h-14 sm:w-16 sm:h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <VideoOff className="w-7 h-7 sm:w-8 sm:h-8 text-red-400" />
                </div>
                <h3 className="text-base sm:text-lg font-bold text-white mb-2">Connection Failed</h3>
                <p className="text-slate-400 mb-4 text-sm sm:text-base">Unable to connect to the video call.</p>
                <Button
                  onClick={initializeEmbeddedCall}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-2 rounded-lg"
                >
                  Try Again
                </Button>
              </div>
            </div>
          )}

          {/* Daily.co video container */}
          <div 
            ref={videoContainerRef} 
            data-testid="daily-video-container"
            className="w-full h-full"
            style={{ display: callState === 'joined' || callState === 'joining' ? 'block' : 'none' }}
          />

          {/* Video Controls - Bottom center */}
          {callState === 'joined' && (
            <div className="absolute bottom-3 sm:bottom-4 left-1/2 transform -translate-x-1/2 flex items-center space-x-2 sm:space-x-3 bg-slate-800/90 backdrop-blur-sm px-4 sm:px-6 py-2 sm:py-3 rounded-full">
              <Button
                onClick={toggleMic}
                data-testid="toggle-mic-btn"
                variant="ghost"
                size="sm"
                className={`rounded-full p-2 sm:p-3 ${isMicOn ? 'bg-slate-700 text-white' : 'bg-red-500 text-white'}`}
              >
                {isMicOn ? <Mic className="w-4 h-4 sm:w-5 sm:h-5" /> : <MicOff className="w-4 h-4 sm:w-5 sm:h-5" />}
              </Button>
              <Button
                onClick={toggleCam}
                data-testid="toggle-cam-btn"
                variant="ghost"
                size="sm"
                className={`rounded-full p-2 sm:p-3 ${isCamOn ? 'bg-slate-700 text-white' : 'bg-red-500 text-white'}`}
              >
                {isCamOn ? <VideoIcon className="w-4 h-4 sm:w-5 sm:h-5" /> : <VideoOff className="w-4 h-4 sm:w-5 sm:h-5" />}
              </Button>
              <Button
                onClick={() => setIsFullscreen(!isFullscreen)}
                data-testid="toggle-fullscreen-btn"
                variant="ghost"
                size="sm"
                className="rounded-full p-2 sm:p-3 bg-slate-700 text-white hover:bg-slate-600"
              >
                {isFullscreen ? <Minimize2 className="w-4 h-4 sm:w-5 sm:h-5" /> : <Maximize2 className="w-4 h-4 sm:w-5 sm:h-5" />}
              </Button>
              <Button
                onClick={leaveCall}
                data-testid="leave-call-btn"
                variant="destructive"
                size="sm"
                className="rounded-full px-4 sm:px-6 py-2 sm:py-3 bg-red-500 hover:bg-red-600"
              >
                <PhoneOff className="w-4 h-4 sm:w-5 sm:h-5" />
                <span className="hidden sm:inline ml-2">Leave</span>
              </Button>
            </div>
          )}

          {/* Chat Toggle Button - Floating */}
          <Button
            onClick={() => setIsChatMinimized(!isChatMinimized)}
            data-testid="toggle-chat-btn"
            variant="ghost"
            size="sm"
            className="absolute top-3 right-3 rounded-full p-2 sm:p-3 bg-slate-800/80 backdrop-blur-sm text-white hover:bg-slate-700"
          >
            {isChatMinimized ? (
              <>
                <PanelRightOpen className="w-4 h-4 sm:w-5 sm:h-5" />
                <span className="ml-2 text-xs hidden sm:inline">Show Chat</span>
              </>
            ) : (
              <>
                <PanelRightClose className="w-4 h-4 sm:w-5 sm:h-5" />
                <span className="ml-2 text-xs hidden sm:inline">Hide Chat</span>
              </>
            )}
          </Button>
        </div>

        {/* Chat Sidebar - Collapsible */}
        <div 
          className={`bg-white rounded-xl border border-slate-200 flex flex-col transition-all duration-300 ${
            isChatMinimized 
              ? 'hidden' 
              : isLandscape 
                ? 'w-80 lg:w-96 flex-shrink-0' 
                : 'h-48 sm:h-64 flex-shrink-0'
          }`}
        >
          <div className="border-b border-slate-200 p-3 sm:p-4 flex items-center justify-between flex-shrink-0">
            <div className="flex items-center space-x-2">
              <MessageCircle className="w-4 h-4 sm:w-5 sm:h-5 text-indigo-600" />
              <h2 className="text-base sm:text-lg font-bold text-slate-900">Live Chat</h2>
            </div>
            <Button
              onClick={() => setIsChatMinimized(true)}
              variant="ghost"
              size="sm"
              className="p-1 text-slate-500 hover:text-slate-700 lg:hidden"
              data-testid="minimize-chat-mobile-btn"
            >
              <PanelRightClose className="w-4 h-4" />
            </Button>
          </div>

          <ScrollArea className="flex-1 p-3 sm:p-4 min-h-0">
            <div data-testid="chat-messages" className="space-y-2 sm:space-y-3">
              {messages.length === 0 ? (
                <p className="text-slate-500 text-xs sm:text-sm text-center py-4 sm:py-8">No messages yet</p>
              ) : (
                messages.map((msg) => (
                  <div key={msg.id} className="bg-slate-50 rounded-lg p-2 sm:p-3">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs sm:text-sm font-semibold text-slate-900">{msg.user}</span>
                      <span className="text-xs text-slate-500">
                        {new Date(msg.timestamp).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                      </span>
                    </div>
                    <p className="text-xs sm:text-sm text-slate-700">{msg.message}</p>
                  </div>
                ))
              )}
            </div>
          </ScrollArea>

          <div className="border-t border-slate-200 p-2 sm:p-4 flex-shrink-0">
            <div className="flex space-x-2">
              <Input
                data-testid="chat-input"
                type="text"
                placeholder="Type a message..."
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                className="flex-1 h-9 sm:h-10 rounded-lg border-slate-200 focus:border-indigo-500 focus:ring-indigo-500/20 text-sm"
              />
              <Button
                data-testid="send-message-btn"
                onClick={sendMessage}
                className="rounded-lg bg-indigo-600 hover:bg-indigo-700 px-3 sm:px-4 h-9 sm:h-10"
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>

        {/* Minimized Chat Indicator - Floating button when chat is hidden */}
        {isChatMinimized && messages.length > 0 && (
          <Button
            onClick={() => setIsChatMinimized(false)}
            className="fixed bottom-20 right-4 rounded-full bg-indigo-600 hover:bg-indigo-700 text-white shadow-lg p-3 sm:p-4"
            data-testid="open-chat-floating-btn"
          >
            <MessageCircle className="w-5 h-5 sm:w-6 sm:h-6" />
            <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
              {messages.length}
            </span>
          </Button>
        )}
      </div>
    </div>
  );
}
